// ========================================================
// FAST HIT Website — Tournaments Service (Phase 7)
// Tournament discovery, groups, standings, fixtures, registrations
// Real Firestore data only — no dummy fallbacks
// ========================================================

import {
  collection,
  query,
  where,
  limit,
  getDocs,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  serverTimestamp,
  onSnapshot,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../types/collections';
import type {
  Tournament,
  TournamentGroup,
  TournamentStanding,
  TournamentFixture,
  TournamentRegistration,
} from '../types';

export function mapDocToTournament(id: string, data: DocumentData): Tournament {
  return {
    id,
    name: data.name || 'Cricket Championship',
    season: data.season || '2026',
    status: data.status || 'registration',
    format: data.format || 'T20',
    startDate: data.startDate?.toDate?.() || (data.startDate ? new Date(data.startDate) : new Date()),
    endDate: data.endDate?.toDate?.() || (data.endDate ? new Date(data.endDate) : undefined),
    bannerUrl: data.bannerUrl || data.banner,
    logoUrl: data.logoUrl || data.logo,
    organizer: {
      id: data.organizer?.id || data.organizerId || 'org-fast-hit',
      name: data.organizer?.name || data.organizerName || 'Tournament Committee',
      contactEmail: data.organizer?.contactEmail || data.contactEmail,
      contactPhone: data.organizer?.contactPhone || data.contactPhone,
    },
    teamsCount: Number(data.teamsCount || 8),
    matchesTotal: Number(data.matchesTotal || 15),
    matchesCompleted: Number(data.matchesCompleted || 0),
    location: data.location || 'Cricket Ground',
    prizePool: data.prizePool || '₹50,000',
    description: data.description || '',
    rules: data.rules || 'Standard ICC Cricket rules apply.',
    createdBy: data.createdBy || 'user-1',
    createdAt: data.createdAt?.toDate?.() || new Date(),
    registrationOpen: Boolean(data.registrationOpen ?? true),
    entryFee: data.entryFee || '₹5,000',
    ballType: data.ballType || 'Leather',
  };
}

/**
 * Fetch tournaments with optional status, format, and search query filters.
 * Returns real Firestore data or empty array.
 */
export async function getTournaments(filter?: {
  status?: string;
  format?: string;
  query?: string;
}): Promise<Tournament[]> {
  try {
    const coll = collection(db, COLLECTIONS.TOURNAMENTS);
    let q = query(coll, limit(50));

    if (filter?.status && filter.status !== 'all') {
      q = query(coll, where('status', '==', filter.status), limit(50));
    }

    const snap = await getDocs(q);
    let list = snap.docs.map((d) => mapDocToTournament(d.id, d.data()));

    if (filter?.format && filter.format !== 'all') {
      list = list.filter((t) => t.format === filter.format);
    }

    if (filter?.query && filter.query.trim()) {
      const search = filter.query.toLowerCase();
      list = list.filter(
        (t) =>
          t.name.toLowerCase().includes(search) ||
          t.location.toLowerCase().includes(search) ||
          t.organizer.name.toLowerCase().includes(search)
      );
    }

    return list;
  } catch (err) {
    console.warn('Could not query tournaments from Firestore:', err);
    return [];
  }
}

/**
 * Fetch featured/spotlight tournaments for the landing page.
 */
export async function getSpotlightTournaments(maxItems = 3): Promise<Tournament[]> {
  try {
    const coll = collection(db, COLLECTIONS.TOURNAMENTS);
    const q = query(coll, limit(maxItems));
    const snap = await getDocs(q);
    return snap.docs.map((d) => mapDocToTournament(d.id, d.data()));
  } catch (err) {
    console.warn('Could not query spotlight tournaments from Firestore:', err);
    return [];
  }
}

/**
 * Real-time tournaments subscription
 */
export function subscribeTournaments(
  callback: (tournaments: Tournament[]) => void,
  maxItems = 20
): Unsubscribe {
  const coll = collection(db, COLLECTIONS.TOURNAMENTS);
  const q = query(coll, limit(maxItems));

  return onSnapshot(
    q,
    (snap) => {
      const list = snap.docs.map((d) => mapDocToTournament(d.id, d.data()));
      callback(list);
    },
    (err) => {
      console.warn('Tournaments subscription error:', err);
      callback([]);
    }
  );
}

/**
 * Fetch tournament by ID
 */
export async function getTournamentById(id: string): Promise<Tournament | null> {
  try {
    const docRef = doc(db, COLLECTIONS.TOURNAMENTS, id);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      return mapDocToTournament(snap.id, snap.data());
    }
  } catch (err) {
    console.warn('Could not query tournament detail from Firestore:', err);
  }
  return null;
}

/**
 * Fetch tournament group standings
 */
export async function getTournamentStandings(tournamentId: string): Promise<TournamentGroup[]> {
  try {
    const standingsRef = collection(db, COLLECTIONS.TOURNAMENTS, tournamentId, 'standings');
    const snap = await getDocs(standingsRef);
    if (!snap.empty) {
      const groupsMap: Record<string, TournamentStanding[]> = {};
      snap.docs.forEach((d) => {
        const data = d.data();
        const gName = data.groupName || 'Group A';
        if (!groupsMap[gName]) groupsMap[gName] = [];
        groupsMap[gName].push({
          teamId: data.teamId || d.id,
          teamName: data.teamName || 'Team',
          played: Number(data.played || 0),
          won: Number(data.won || 0),
          lost: Number(data.lost || 0),
          tied: Number(data.tied || 0),
          noResult: Number(data.noResult || 0),
          points: Number(data.points || 0),
          netRunRate: Number(data.netRunRate || 0),
          runsScored: Number(data.runsScored || 0),
          oversFaced: Number(data.oversFaced || 0),
          runsConceded: Number(data.runsConceded || 0),
          oversBowled: Number(data.oversBowled || 0),
        });
      });

      return Object.entries(groupsMap).map(([groupName, standings], idx) => ({
        id: `group-${idx}`,
        name: groupName,
        standings: standings.sort((a, b) => b.points - a.points || b.netRunRate - a.netRunRate),
      }));
    }
  } catch (err) {
    console.warn('Could not query standings:', err);
  }
  return [];
}

/**
 * Fetch tournament fixtures
 */
export async function getTournamentFixtures(tournamentId: string): Promise<TournamentFixture[]> {
  try {
    const matchesRef = collection(db, COLLECTIONS.MATCHES);
    const q = query(matchesRef, where('tournamentId', '==', tournamentId), limit(50));
    const snap = await getDocs(q);

    if (!snap.empty) {
      return snap.docs.map((d, index) => {
        const data = d.data();
        return {
          id: d.id,
          matchNumber: Number(data.matchNumber || index + 1),
          teamAId: data.teamA?.id || '',
          teamAName: data.teamA?.name || 'Team 1',
          teamALogo: data.teamA?.logoUrl,
          teamBId: data.teamB?.id || '',
          teamBName: data.teamB?.name || 'Team 2',
          teamBLogo: data.teamB?.logoUrl,
          scheduledAt: data.scheduledAt?.toDate?.() || new Date(),
          groundName: data.groundName || data.ground || 'Stadium Ground',
          city: data.city || 'City',
          stage: data.stage || 'League Stage',
          status: data.status || 'upcoming',
          resultSummary: data.resultString,
          matchId: d.id,
        };
      });
    }
  } catch (err) {
    console.warn('Could not query tournament fixtures:', err);
  }
  return [];
}

/**
 * Fetch tournament leaderboard
 */
export async function getTournamentLeaderboard(tournamentId: string) {
  try {
    const lbRef = doc(db, COLLECTIONS.LEADERBOARDS, tournamentId);
    const snap = await getDoc(lbRef);
    if (snap.exists()) {
      const data = snap.data();
      return {
        topBatters: data.topBatters || [],
        topBowlers: data.topBowlers || [],
      };
    }
  } catch (err) {
    console.warn('Could not query tournament leaderboard:', err);
  }
  return {
    topBatters: [],
    topBowlers: [],
  };
}

/**
 * Register a team for an open tournament
 */
export async function registerTeamForTournament(
  data: Omit<TournamentRegistration, 'id' | 'registeredAt' | 'status'>
): Promise<TournamentRegistration> {
  const newRegistration: TournamentRegistration = {
    ...data,
    id: `reg-${Date.now()}`,
    status: 'pending',
    registeredAt: new Date(),
  };

  try {
    const docRef = await addDoc(collection(db, 'tournament_registrations'), {
      ...newRegistration,
      registeredAt: serverTimestamp(),
    });
    newRegistration.id = docRef.id;
  } catch (err) {
    console.warn('Could not save registration to Firestore:', err);
  }

  return newRegistration;
}

/**
 * Fetch registrations for tournament (organizer view)
 */
export async function getTournamentRegistrations(tournamentId: string): Promise<TournamentRegistration[]> {
  try {
    const coll = collection(db, 'tournament_registrations');
    let q = query(coll, limit(50));
    if (tournamentId && tournamentId !== 'all') {
      q = query(coll, where('tournamentId', '==', tournamentId), limit(50));
    }
    const snap = await getDocs(q);
    return snap.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        tournamentId: data.tournamentId || '',
        teamId: data.teamId || '',
        teamName: data.teamName || 'Team',
        captainName: data.captainName || 'Captain',
        contactPhone: data.contactPhone || '',
        squadSize: Number(data.squadSize || 15),
        status: data.status || 'pending',
        registeredAt: data.registeredAt?.toDate?.() || new Date(),
      };
    });
  } catch (err) {
    console.warn('Could not query tournament registrations:', err);
    return [];
  }
}

/**
 * Approve or reject team registration
 */
export async function updateRegistrationStatus(
  registrationId: string,
  status: 'approved' | 'rejected'
): Promise<boolean> {
  try {
    const docRef = doc(db, 'tournament_registrations', registrationId);
    await updateDoc(docRef, {
      status,
      reviewedAt: serverTimestamp(),
    });
    return true;
  } catch (err) {
    console.warn('Could not update registration status:', err);
    return false;
  }
}

/**
 * Create tournament (organizer workflow)
 */
export async function createTournament(
  tournamentData: Partial<Tournament>
): Promise<Tournament> {
  const docData = {
    name: tournamentData.name || 'New Tournament',
    season: tournamentData.season || '2026',
    status: tournamentData.status || 'registration',
    format: tournamentData.format || 'T20',
    startDate: tournamentData.startDate || new Date(),
    endDate: tournamentData.endDate || null,
    organizer: tournamentData.organizer || {
      id: 'org-user',
      name: 'Tournament Committee',
    },
    organizerName: tournamentData.organizer?.name || 'Tournament Committee',
    city: tournamentData.location || 'Local Cricket Ground',
    teamsCount: tournamentData.teamsCount || 8,
    teamCount: 0,
    maxTeams: tournamentData.teamsCount || 16,
    overs: 20,
    matchesTotal: tournamentData.matchesTotal || 15,
    matchesCompleted: 0,
    location: tournamentData.location || 'Local Cricket Ground',
    prizePool: tournamentData.prizePool || '₹50,000',
    description: tournamentData.description || '',
    rules: tournamentData.rules || 'Standard ICC T20 rules.',
    createdBy: tournamentData.createdBy || 'user-1',
    createdAt: serverTimestamp(),
    registrationOpen: tournamentData.registrationOpen ?? true,
    entryFee: tournamentData.entryFee || '₹5,000',
    ballType: tournamentData.ballType || 'Leather',
  };

  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.TOURNAMENTS), docData);
    return mapDocToTournament(docRef.id, docData);
  } catch (err) {
    console.warn('Could not create tournament in Firestore:', err);
    return mapDocToTournament(`t-${Date.now()}`, docData);
  }
}
